import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../../utils/api'
import { BaseWidget } from './BaseWidget'
import { CalculatorIcon } from '../Icons'
import { DesignTheme } from './FinancialWidgetDesignSelector'
import { useDashboard } from '../../contexts/DashboardContext'

interface AverageBpsWidgetProps {
  className?: string
  designTheme?: DesignTheme
  widgetId?: string
}

interface BpsData {
  bps: number
  totalRawCommission: number
  totalAmountFinanced: number
  dealCount: number
}

interface BpsMetrics {
  current: BpsData
  previousYear: BpsData
  period: string
}

const AverageBpsWidget: React.FC<AverageBpsWidgetProps> = ({ className, designTheme = 'minimal', widgetId }) => {
  const { t } = useTranslation()
  const [data, setData] = useState<BpsData | null>(null)
  const [previousYearData, setPreviousYearData] = useState<BpsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [isMinimal, setIsMinimal] = useState(false)
  
  const { getWidgetParameter, updateWidgetParameter } = useDashboard()
  const period = getWidgetParameter(widgetId || 'average_bps', 'period', 'month-to-date')

  // Detect widget height to determine if we should use minimal display
  useEffect(() => {
    const checkHeight = () => {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'average_bps'}"]`)
      if (widgetElement) {
        const height = widgetElement.clientHeight
        setIsMinimal(height <= 230)
      }
    }
    
    checkHeight()
    const timer = setTimeout(checkHeight, 100)
    window.addEventListener('resize', checkHeight)
    const observer = new MutationObserver(checkHeight)
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'average_bps'}"]`)
    if (widgetElement) {
      observer.observe(widgetElement, { attributes: true, attributeFilter: ['style'] })
    }
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkHeight)
      observer.disconnect()
    }
  }, [widgetId])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatAmount = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M$`
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K$`
    }
    return formatCurrency(amount)
  }

  const calculateYoYChange = (current: number, previous: number) => {
    if (previous === 0) return 0
    return ((current - previous) / previous) * 100
  }

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/deals/average-bps?period=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const metrics: BpsMetrics = await response.json()
        setData(metrics.current)
        setPreviousYearData(metrics.previousYear || null)
      }
    } catch (error) {
      console.error('Error fetching average BPS data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [period])

  const yoyChange = (previousYearData && data) ? calculateYoYChange(data.bps, previousYearData.bps) : null

  const actions = (
    <select
      value={period}
      onChange={(e) => updateWidgetParameter(widgetId || 'average_bps', 'period', e.target.value)}
      className="text-xs bg-white/20 text-white border border-white/30 rounded px-2 py-1"
    >
      <option value="month-to-date">{t('dashboard.widgets.monthToDate')}</option>
      <option value="year-to-date">{t('dashboard.widgets.yearToDate')}</option>
    </select>
  )

  const footer = yoyChange !== null ? (
    <div className="text-white/90">
      <div className="flex items-center space-x-1.5">
        <span className={`text-xs font-medium ${yoyChange > 0 ? 'text-white' : yoyChange < 0 ? 'text-red-200' : 'text-white/70'}`}>
          {yoyChange > 0 ? '↗' : yoyChange < 0 ? '↘' : ''} {yoyChange > 0 ? '+' : ''}{yoyChange.toFixed(1)}%
        </span>
        <span className="text-xs text-white/70">{t('dashboard.vsLastYear')}</span>
      </div>
    </div>
  ) : null

  return (
    <BaseWidget
      title={t('dashboard.widgets.averageBps')}
      icon={<CalculatorIcon className="h-5 w-5" />}
      accent="analytics"
      variant="gradient"
      className={className}
      loading={loading || !data}
      actions={actions}
      footer={footer}
      minimalDisplay={isMinimal}
    >
      {isMinimal ? (
        // Minimal display - only show the main BPS metric
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="text-3xl font-bold text-white tabular-nums">
              {data?.bps.toFixed(2)} {t('dashboard.widgets.bps')}
            </div>
            <div className="text-xs text-white/70 uppercase tracking-wider mt-1">
              {t('dashboard.widgets.averageCommissionRate')}
            </div>
          </div>
        </div>
      ) : (
        // Full display with all details
        <div className="space-y-2">
          <div className="text-center">
            <div className="text-2xl font-bold text-white tabular-nums mb-1">
              {data?.bps.toFixed(2)} {t('dashboard.widgets.bps')}
            </div>
            <div className="text-xs text-white/80 uppercase tracking-wider font-semibold">
              {t('dashboard.widgets.averageCommissionRate')}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-transparent border border-white/20 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-white">{data?.dealCount || 0}</div>
              <div className="text-xs text-white/70">{t('dashboard.widgets.deals')}</div>
            </div>
            <div className="bg-transparent border border-white/20 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-white">{formatAmount(data?.totalAmountFinanced || 0)}</div>
              <div className="text-xs text-white/70">{t('dashboard.widgets.financed')}</div>
            </div>
          </div>
        </div>
      )}
    </BaseWidget>
  )
}

export default AverageBpsWidget

